export const ROLES = {
    SUPER_ADMIN: 'super_admin',
    ADMIN: 'admin',
    CEO: 'ceo',
    SALES_EXECUTIVE: 'sales_executive',
    ACCOUNT_MANAGER: 'account_manager',
    DELIVERY_MANAGER: 'delivery_manager',
    FINANCE: 'finance',
    MARKETING: 'marketing',
    CRM_ADMINISTRATOR: 'crm_administrator',
    VIEWER: 'viewer'
};

export const ROLE_LABELS = {
    super_admin: 'System Administrator',
    admin: 'Executive',
    ceo: 'Sales Manager',
    sales_executive: 'Sales Executive',
    account_manager: 'Account Manager',
    delivery_manager: 'Delivery Manager',
    finance: 'Finance',
    marketing: 'Marketing',
    crm_administrator: 'CRM Administrator',
    viewer: 'Viewer',
    // Legacy aliases (display only)
    sales: 'Sales Executive',
    sales_representative: 'Sales Executive',
};

export const ROLE_ORDER = [
    'super_admin',
    'admin',
    'ceo',
    'sales_executive',
    'account_manager',
    'delivery_manager',
    'finance',
    'marketing',
    'crm_administrator',
    'viewer'
];

/** Data scope levels for hierarchical access */
export const DATA_SCOPE = {
    OWN: 'own',
    TEAM: 'team',
    DEPARTMENT: 'department',
    COMPANY: 'company',
    ASSIGNED: 'assigned'
};

/** Map legacy stored roles to current permission keys */
export const LEGACY_ROLE_ALIASES = {
    sales: 'sales_executive',
    executive: 'viewer',
};

export function normalizeRole(role) {
    return LEGACY_ROLE_ALIASES[role] || role;
}

/**
 * Get data scope for a role
 */
export function getDataScope(role) {
    const normalizedRole = normalizeRole(role);
    
    switch (normalizedRole) {
        case 'super_admin':
        case 'admin':
        case 'ceo':
        case 'crm_administrator':
            return DATA_SCOPE.COMPANY;

        case 'sales_executive':
        case 'sales_representative':
        case 'account_manager':
        case 'delivery_manager':
        case 'finance':
        case 'marketing':
            return DATA_SCOPE.OWN;

        case 'viewer':
            return DATA_SCOPE.COMPANY;

        default:
            return DATA_SCOPE.OWN;
    }
}

/**
 * Check if role is admin-level
 */
export function isAdminRole(role) {
    const normalizedRole = normalizeRole(role);
    return normalizedRole === 'admin' || normalizedRole === 'super_admin';
}

/**
 * Check if role is C-level executive
 */
export function isCLevel(role) {
    const normalizedRole = normalizeRole(role);
    return normalizedRole === 'ceo';
}

let _roleMetaByName = {};

export function setRoleMetadata(roles) {
    _roleMetaByName = Object.fromEntries(
        (roles || []).map(r => [r.name ?? r.role ?? r.value, r])
    );
}

export function getRoleMetadata(role) {
    return _roleMetaByName[normalizeRole(role)] || null;
}

export function isSystemRole(role) {
    const meta = getRoleMetadata(role);
    if (meta && typeof meta.is_system === 'boolean') {
        return meta.is_system;
    }
    const name = normalizeRole(role);
    return name === 'super_admin' || name === 'admin';
}

export function filterAssignableUsers(users, { includeUserIds = [] } = {}) {
    const keep = new Set((includeUserIds || []).filter(Boolean));
    return (users || []).filter(u =>
        u.status === 'active' &&
        (!isSystemRole(u.role) || keep.has(u.id))
    );
}

export function filterCommissionEligibleUsers(users, options = {}) {
    return filterAssignableUsers(users, options).filter(u => u.isCommissionEligible !== false);
}

export function formatAssignableUserLabel(user, { legacyOwnerIds = new Set() } = {}) {
    const base = user.name || user.email || user.id;
    if (!legacyOwnerIds.has(user.id)) return base;
    const roleLabel = ROLE_LABELS[user.role] || user.role;
    return `${base} (${roleLabel} — Legacy Owner)`;
}
